// Themed replacements for window.confirm / window.alert / window.prompt.
//
// The native WKWebView dialogs (with the site origin in the title bar) broke
// the dark premium feel everywhere the app asked for a confirmation — report,
// block, delete, discard, cancel-subscription. These render the same visual
// language as the in-app modals (see PostCard's delete modal) and return
// promises so call sites read almost like the originals:
//
//   if (!(await bamConfirm({ title: "Delete this?", danger: true }))) return;
//   bamToast("Saved.");
//
// Each call mounts its own React root on a detached host div and cleans up
// after itself — no provider wiring needed, callable from any code path.
import React, { useEffect, useState } from "react";
import { createRoot } from "react-dom/client";

const GOLD = "#E2DD9F";

function ConfirmSheet({ title, message, confirmLabel, cancelLabel, danger, onResolve }) {
  const [leaving, setLeaving] = useState(false);
  // Single-fire guard — a second tap during the 140ms exit animation (or an
  // iOS ghost click) must not resolve again or re-trigger anything.
  const doneRef = React.useRef(false);
  const close = (val) => {
    if (doneRef.current) return;
    doneRef.current = true;
    setLeaving(true);
    setTimeout(() => onResolve(val), 140);
  };
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") close(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div onClick={() => close(false)} className="bam-scrim-in"
      style={{ position: "fixed", inset: 0, zIndex: 100000, background: "rgba(0,0,0,0.6)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
        opacity: leaving ? 0 : 1, transition: "opacity .14s ease" }}>
      <div onClick={(e) => e.stopPropagation()} className="bam-dialog-in"
        style={{ background: "linear-gradient(180deg,#151515,#0E0E0E)", borderRadius: 14,
          padding: "26px 28px", maxWidth: 380, width: "100%",
          border: "1px solid rgba(255,255,255,0.08)",
          boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
          fontFamily: "'DM Sans', -apple-system, sans-serif" }}>
        {title && <div style={{ fontSize: 16, fontWeight: 800, color: "#F2F2F2", marginBottom: message ? 8 : 20 }}>{title}</div>}
        {message && <div style={{ fontSize: 13, color: "#9A9A9A", marginBottom: 22, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{message}</div>}
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button className="btn" onClick={() => close(false)}
            style={{ padding: "9px 20px", borderRadius: 8, fontSize: 13, fontWeight: 700,
              background: "transparent", color: "#AAA", border: "1px solid rgba(255,255,255,0.12)", cursor: "pointer" }}>
            {cancelLabel}
          </button>
          <button className="btn" onClick={() => close(true)}
            style={{ padding: "9px 20px", borderRadius: 8, fontSize: 13, fontWeight: 800,
              background: danger ? "#E06060" : GOLD, color: danger ? "#fff" : "#111",
              border: "none", cursor: "pointer" }}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// The same question already on screen. A native confirm froze the page, so a
// double Enter or a double tap could only ever ask once; these don't block,
// so the second ask would stack a second sheet and run the action twice.
// A repeat of a question that is already open answers "no" straight away,
// which leaves the first sheet as the only way to say yes.
const openConfirms = new Set();

export function bamConfirm({ title = "Are you sure?", message = "", confirmLabel = "Confirm", cancelLabel = "Cancel", danger = false } = {}) {
  const key = `${title}\u0000${message}`;
  if (openConfirms.has(key)) return Promise.resolve(false);
  openConfirms.add(key);
  return new Promise((resolve) => {
    const host = document.createElement("div");
    document.body.appendChild(host);
    const root = createRoot(host);
    const onResolve = (val) => {
      openConfirms.delete(key);
      resolve(val);
      setTimeout(() => { try { root.unmount(); host.remove(); } catch {} }, 0);
    };
    root.render(<ConfirmSheet title={title} message={message} confirmLabel={confirmLabel} cancelLabel={cancelLabel} danger={danger} onResolve={onResolve} />);
  });
}

// The window.prompt replacement. Same sheet as ConfirmSheet with one dark
// input. Input text is 16px so iOS doesn't zoom the page on focus, and the
// phone rule gives the input and buttons 44px touch targets.
function PromptSheet({ title, message, placeholder, defaultValue, confirmLabel, cancelLabel, multiline, onResolve }) {
  const [leaving, setLeaving] = useState(false);
  const [value, setValue] = useState(defaultValue);
  const inputRef = React.useRef(null);
  const doneRef = React.useRef(false);
  const close = (val) => {
    if (doneRef.current) return;
    doneRef.current = true;
    setLeaving(true);
    setTimeout(() => onResolve(val), 140);
  };
  const submit = () => close(String(inputRef.current ? inputRef.current.value : value).trim());
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") close(null); };
    document.addEventListener("keydown", onKey);
    const el = inputRef.current;
    if (el) { el.focus(); if (el.value) el.select(); }
    return () => document.removeEventListener("keydown", onKey);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const onInputKey = (e) => {
    // isComposing: Enter that confirms an IME candidate must not submit.
    if (e.key !== "Enter" || e.nativeEvent.isComposing) return;
    if (multiline && e.shiftKey) return;
    e.preventDefault();
    submit();
  };
  // Keystrokes stay inside the sheet, like a native prompt: page-level
  // shortcuts on window/document (approve on Cmd+Enter, "?" for help) must
  // not fire while someone types here. React's stopPropagation also stops
  // the native event at this sheet's own root, before document and window.
  const onCardKey = (e) => {
    e.stopPropagation();
    if (e.key === "Escape") close(null);
  };
  const inputStyle = {
    display: "block", width: "100%", boxSizing: "border-box", marginBottom: 20,
    padding: "11px 12px", borderRadius: 8, fontSize: 16, lineHeight: 1.45,
    fontFamily: "inherit", color: "#F2F2F2", background: "#0A0A0A",
    border: "1px solid rgba(255,255,255,0.12)", outline: "none",
    resize: multiline ? "vertical" : "none",
  };
  return (
    <div onClick={() => close(null)} className="bam-scrim-in"
      style={{ position: "fixed", inset: 0, zIndex: 100000, background: "rgba(0,0,0,0.6)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
        opacity: leaving ? 0 : 1, transition: "opacity .14s ease" }}>
      <style>{`
        .bam-prompt-input::placeholder { color: #5A5A5A; }
        .bam-prompt-input:focus { border-color: ${GOLD}88 !important; }
        @media (max-width: 600px) {
          .bam-prompt-card { padding: 22px 20px !important; }
          .bam-prompt-input { min-height: 44px; }
          .bam-prompt-btn { min-height: 44px; flex: 1; }
        }
      `}</style>
      <div role="dialog" aria-modal="true" aria-label={title || "Input"} className="bam-prompt-card bam-dialog-in"
        onClick={(e) => e.stopPropagation()} onKeyDown={onCardKey}
        style={{ background: "linear-gradient(180deg,#151515,#0E0E0E)", borderRadius: 14,
          padding: "26px 28px", maxWidth: 380, width: "100%", boxSizing: "border-box",
          border: "1px solid rgba(255,255,255,0.08)",
          boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
          fontFamily: "'DM Sans', -apple-system, sans-serif" }}>
        {title && <div style={{ fontSize: 16, fontWeight: 800, color: "#F2F2F2", marginBottom: message ? 8 : 14 }}>{title}</div>}
        {message && <div style={{ fontSize: 13, color: "#9A9A9A", marginBottom: 14, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{message}</div>}
        {multiline ? (
          <textarea ref={inputRef} className="bam-prompt-input" rows={4} value={value}
            placeholder={placeholder} onChange={(e) => setValue(e.target.value)} onKeyDown={onInputKey}
            style={inputStyle} />
        ) : (
          <input ref={inputRef} className="bam-prompt-input" type="text" value={value}
            placeholder={placeholder} onChange={(e) => setValue(e.target.value)} onKeyDown={onInputKey}
            enterKeyHint="done" style={inputStyle} />
        )}
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button className="btn bam-prompt-btn" onClick={() => close(null)}
            style={{ padding: "9px 20px", borderRadius: 8, fontSize: 13, fontWeight: 700,
              background: "transparent", color: "#AAA", border: "1px solid rgba(255,255,255,0.12)", cursor: "pointer" }}>
            {cancelLabel}
          </button>
          <button className="btn bam-prompt-btn" onClick={submit}
            style={{ padding: "9px 20px", borderRadius: 8, fontSize: 13, fontWeight: 800,
              background: GOLD, color: "#111", border: "none", cursor: "pointer" }}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// Resolves to the trimmed string (possibly ""), or null on Cancel, Escape or
// a tap outside, the same null/"" split window.prompt gives call sites.
//
//   const reason = await bamPrompt({ title: "Reason?", multiline: true });
//   if (reason === null) return;
export function bamPrompt({ title = "", message = "", placeholder = "", defaultValue = "", confirmLabel = "OK", cancelLabel = "Cancel", multiline = false } = {}) {
  return new Promise((resolve) => {
    const host = document.createElement("div");
    document.body.appendChild(host);
    const root = createRoot(host);
    const onResolve = (val) => {
      resolve(val);
      setTimeout(() => { try { root.unmount(); host.remove(); } catch {} }, 0);
    };
    root.render(<PromptSheet title={title} message={message} placeholder={placeholder}
      defaultValue={defaultValue == null ? "" : String(defaultValue)} confirmLabel={confirmLabel}
      cancelLabel={cancelLabel} multiline={multiline} onResolve={onResolve} />);
  });
}

function Toast({ message, tone, onDone }) {
  const [leaving, setLeaving] = useState(false);
  useEffect(() => {
    const t1 = setTimeout(() => setLeaving(true), 3300);
    const t2 = setTimeout(onDone, 3600);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [onDone]);
  const border = tone === "error" ? "rgba(224,96,96,0.5)" : `${GOLD}44`;
  const accent = tone === "error" ? "#E06060" : GOLD;
  return (
    <div style={{ position: "fixed", left: "50%", transform: `translateX(-50%) translateY(${leaving ? 12 : 0}px)`,
      bottom: "calc(env(safe-area-inset-bottom, 0px) + 84px)", zIndex: 100000,
      background: "rgba(18,18,18,0.97)", border: `1px solid ${border}`, borderRadius: 12,
      padding: "11px 18px", maxWidth: "min(92vw, 420px)",
      boxShadow: "0 10px 36px rgba(0,0,0,0.5)",
      fontFamily: "'DM Sans', -apple-system, sans-serif",
      display: "flex", alignItems: "center", gap: 9,
      opacity: leaving ? 0 : 1, transition: "opacity .3s ease, transform .3s ease",
      animation: "bamToastIn .28s cubic-bezier(.2,.9,.3,1.1)" }}>
      <style>{`@keyframes bamToastIn { from { opacity: 0; transform: translateX(-50%) translateY(14px); } to { opacity: 1; transform: translateX(-50%) translateY(0); } }`}</style>
      <span aria-hidden style={{ width: 7, height: 7, borderRadius: "50%", background: accent, flexShrink: 0 }} />
      <span style={{ fontSize: 13, color: "#E8E8E8", lineHeight: 1.45 }}>{message}</span>
    </div>
  );
}

export function bamToast(message, { tone = "info" } = {}) {
  const host = document.createElement("div");
  document.body.appendChild(host);
  const root = createRoot(host);
  const onDone = () => { try { root.unmount(); host.remove(); } catch {} };
  root.render(<Toast message={message} tone={tone} onDone={onDone} />);
}
